import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ChevronLeft,
  Plus,
  Trash2,
  Save,
  Play,
  CheckCircle2,
  Flag,
  GripVertical,
  AlertTriangle,
} from "lucide-react";

/**
 * Visual scenario builder. URL: /admin/scenarios/new or /admin/scenarios/:id
 *
 * Layout: 2 columns.
 *   Left  — node list (click to focus). Each node card shows id, prompt preview,
 *           # options, "ending" badge, delete button.
 *   Right — focused node editor. Edit prompt, options (text/correct/points/
 *           feedback/next_node_id dropdown), ending flag + label.
 *
 * Header has title/description/pass_points + Save + Test (only when saved).
 */
const newNodeId = () => `n_${Math.random().toString(36).slice(2, 7)}`;
const newOptId = () => `opt_${Math.random().toString(36).slice(2, 6)}`;

const blankScenario = () => ({
  title: "",
  description: "",
  pass_points: 30,
  course_id: "",
  start_node_id: "n_start",
  nodes: [
    {
      id: "n_start",
      prompt: "",
      options: [],
      ending: false,
      ending_label: "",
    },
  ],
});

export default function AdminScenarioEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = id && id !== "new";

  const [scenario, setScenario] = useState(blankScenario());
  const [focusedNodeId, setFocusedNodeId] = useState("n_start");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!isEdit);
  const [savedId, setSavedId] = useState(isEdit ? id : null);
  const [courses, setCourses] = useState([]);

  // Fetch course list once for the linker dropdown
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/courses");
        setCourses(Array.isArray(r.data) ? r.data : []);
      } catch (_) {}
    })();
  }, []);

  // Load existing scenario
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const r = await api.get(`/scenarios/${id}`);
        setScenario({
          title: r.data.title || "",
          description: r.data.description || "",
          pass_points: r.data.pass_points || 30,
          course_id: r.data.course_id || "",
          start_node_id: r.data.start_node_id || "n_start",
          nodes: (r.data.nodes || []).map((n) => ({
            id: n.id,
            prompt: n.prompt || "",
            options: (n.options || []).map((o) => ({
              id: o.id,
              text: o.text || "",
              is_correct: !!o.is_correct,
              points: o.points || 0,
              feedback: o.feedback || "",
              next_node_id: o.next_node_id || "",
            })),
            ending: !!n.ending,
            ending_label: n.ending_label || "",
          })),
        });
        setFocusedNodeId(r.data.start_node_id || (r.data.nodes?.[0]?.id ?? "n_start"));
      } catch (_) {
        toast.error("Scenario not found");
        navigate("/admin/scenarios");
      } finally {
        setLoaded(true);
      }
    })();
  }, [id, isEdit, navigate]);

  const focusedNode = useMemo(
    () => scenario.nodes.find((n) => n.id === focusedNodeId) || null,
    [scenario.nodes, focusedNodeId],
  );

  // ── Mutators ─────────────────────────────────────────────────────────────
  const patchScenario = (patch) => setScenario((s) => ({ ...s, ...patch }));

  const addNode = () => {
    const id = newNodeId();
    setScenario((s) => ({
      ...s,
      nodes: [...s.nodes, { id, prompt: "", options: [], ending: false, ending_label: "" }],
    }));
    setFocusedNodeId(id);
  };

  const removeNode = (nid) => {
    if (nid === scenario.start_node_id) {
      toast.error("Cannot delete the start node.");
      return;
    }
    if (scenario.nodes.length <= 1) {
      toast.error("Scenario needs at least one node.");
      return;
    }
    if (!window.confirm("Delete this node?")) return;
    setScenario((s) => ({
      ...s,
      // Also clear any options pointing here
      nodes: s.nodes.filter((n) => n.id !== nid).map((n) => ({
        ...n,
        options: n.options.map((o) => o.next_node_id === nid ? { ...o, next_node_id: "" } : o),
      })),
    }));
    if (focusedNodeId === nid) setFocusedNodeId(scenario.start_node_id);
  };

  const patchNode = (nid, patch) => {
    setScenario((s) => ({
      ...s,
      nodes: s.nodes.map((n) => n.id === nid ? { ...n, ...patch } : n),
    }));
  };

  const addOption = (nid) => {
    const opt = { id: newOptId(), text: "", is_correct: false, points: 0, feedback: "", next_node_id: "" };
    patchNode(nid, { options: [...(scenario.nodes.find((n) => n.id === nid)?.options || []), opt] });
  };

  const patchOption = (nid, oid, patch) => {
    setScenario((s) => ({
      ...s,
      nodes: s.nodes.map((n) => n.id === nid ? {
        ...n,
        options: n.options.map((o) => o.id === oid ? { ...o, ...patch } : o),
      } : n),
    }));
  };

  const removeOption = (nid, oid) => {
    patchNode(nid, { options: focusedNode.options.filter((o) => o.id !== oid) });
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = [];
    if (!scenario.title.trim()) errs.push("Title is required.");
    if (!scenario.nodes.find((n) => n.id === scenario.start_node_id)) {
      errs.push("Start node not found.");
    }
    scenario.nodes.forEach((n, i) => {
      if (!n.prompt.trim()) errs.push(`Node ${n.id}: prompt is empty.`);
      if (!n.ending && n.options.length === 0) {
        errs.push(`Node ${n.id}: needs at least one option or must be marked as ending.`);
      }
      n.options.forEach((o, j) => {
        if (!o.text.trim()) errs.push(`Node ${n.id} · option ${j + 1}: text is empty.`);
        if (o.next_node_id && !scenario.nodes.find((nn) => nn.id === o.next_node_id)) {
          errs.push(`Node ${n.id} · option ${j + 1}: next_node_id "${o.next_node_id}" does not exist.`);
        }
      });
    });
    return errs;
  };

  const save = async () => {
    const errs = validate();
    if (errs.length) {
      toast.error(errs[0]);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: scenario.title.trim(),
        description: scenario.description.trim(),
        pass_points: Number(scenario.pass_points) || 0,
        course_id: scenario.course_id || null,
        start_node_id: scenario.start_node_id,
        nodes: scenario.nodes,
      };
      let saved;
      if (savedId) {
        const r = await api.put(`/scenarios/${savedId}`, payload);
        saved = r.data;
        toast.success("Scenario updated");
      } else {
        const r = await api.post("/scenarios", payload);
        saved = r.data;
        setSavedId(saved.id);
        toast.success("Scenario created");
        // Update URL without remounting
        window.history.replaceState({}, "", `/admin/scenarios/${saved.id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error saving");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div className="text-[#374151]">Loading editor...</div>;

  const errs = validate();

  return (
    <div data-testid="scenario-editor">
      <Link to="/admin/scenarios" className="inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#1B2E7A] mb-3">
        <ChevronLeft className="h-3.5 w-3.5" /> Back to scenarios
      </Link>

      {/* Header */}
      <div className="bg-white border border-[#D1D5DB] rounded-xl p-6 mb-6">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#F4B81F] font-medium mb-1">
              {savedId ? "Edit scenario" : "New scenario"}
            </p>
            <h1 className="font-heading text-2xl text-[#1A1A1A]">Scenario builder</h1>
          </div>
          <div className="flex items-center gap-2">
            {savedId && (
              <Link
                to={`/scenarios/${savedId}`}
                target="_blank"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm text-[#1B2E7A] border border-[#1B2E7A] hover:bg-[#EFF6FF]"
                data-testid="editor-test-btn"
              >
                <Play className="h-4 w-4" /> Test scenario
              </Link>
            )}
            <Button
              onClick={save}
              disabled={saving || errs.length > 0}
              className="bg-[#1B2E7A] hover:bg-[#13205A] text-white"
              data-testid="editor-save-btn"
            >
              <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving..." : "Save scenario"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs text-[#374151] mb-1">Title</label>
            <Input
              value={scenario.title}
              onChange={(e) => patchScenario({ title: e.target.value })}
              placeholder="e.g. Customer enters and looks at boots"
              data-testid="editor-title"
            />
          </div>
          <div>
            <label className="block text-xs text-[#374151] mb-1">Pass points</label>
            <Input
              type="number"
              min={0}
              max={500}
              value={scenario.pass_points}
              onChange={(e) => patchScenario({ pass_points: Number(e.target.value) })}
              data-testid="editor-pass-points"
            />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs text-[#374151] mb-1">Description</label>
            <Textarea
              value={scenario.description}
              onChange={(e) => patchScenario({ description: e.target.value })}
              placeholder="Short context for the salesperson (one sentence)"
              rows={2}
              data-testid="editor-description"
            />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs text-[#374151] mb-1">
              Link to course <span className="text-[#9CA3AF]">(optional — appears in the course as a practice scenario)</span>
            </label>
            <select
              value={scenario.course_id || ""}
              onChange={(e) => patchScenario({ course_id: e.target.value })}
              className="w-full border border-[#D1D5DB] rounded-md px-3 py-2 text-sm bg-white"
              data-testid="editor-course-link"
            >
              <option value="">— Standalone scenario (no course) —</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>
        </div>

        {errs.length > 0 && (
          <div className="mt-4 p-3 rounded-md bg-[#FEF3C7] border border-[#F4B81F] text-xs text-[#854D0E] inline-flex items-start gap-2" data-testid="editor-errors">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div>
              <p className="font-bold mb-1">{errs.length} {errs.length === 1 ? "issue" : "issues"} to fix before saving:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                {errs.slice(0, 4).map((e, i) => <li key={i}>{e}</li>)}
                {errs.length > 4 && <li>… and {errs.length - 4} more</li>}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* 2-col body */}
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
        {/* Node list */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-medium">Nodes</p>
            <button
              type="button"
              onClick={addNode}
              className="text-xs text-[#1B2E7A] hover:underline inline-flex items-center gap-1"
              data-testid="editor-add-node"
            >
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>
          <div className="space-y-2">
            {scenario.nodes.map((n) => {
              const isStart = n.id === scenario.start_node_id;
              const isFocused = n.id === focusedNodeId;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => setFocusedNodeId(n.id)}
                  className={`w-full text-left p-3 rounded-md border transition-colors ${
                    isFocused ? "border-[#1B2E7A] bg-[#EFF6FF]" : "border-[#D1D5DB] bg-white hover:border-[#6B7280]"
                  }`}
                  data-testid={`editor-node-tab-${n.id}`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <GripVertical className="h-3 w-3 text-[#9CA3AF]" />
                    <code className="text-[10px] text-[#1B2E7A] font-mono">{n.id}</code>
                    {isStart && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#854D0E] uppercase tracking-[0.16em] font-bold">
                        Start
                      </span>
                    )}
                    {n.ending && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#F3F4F6] text-[#6B7280] uppercase tracking-[0.16em] font-bold inline-flex items-center gap-0.5">
                        <Flag className="h-2.5 w-2.5" /> End
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#1A1A1A] line-clamp-2">
                    {n.prompt || <span className="italic text-[#9CA3AF]">No prompt yet</span>}
                  </p>
                  <p className="text-[10px] text-[#6B7280] mt-1">
                    {n.options.length} {n.options.length === 1 ? "option" : "options"}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Focused node editor */}
        {focusedNode && (
          <div className="bg-white border border-[#D1D5DB] rounded-xl p-6" data-testid="editor-node-form">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-medium">Editing node</p>
                <code className="text-sm text-[#1B2E7A] font-mono">{focusedNode.id}</code>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs inline-flex items-center gap-1.5 text-[#374151] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={focusedNode.ending}
                    onChange={(e) => patchNode(focusedNode.id, { ending: e.target.checked })}
                    className="h-3.5 w-3.5 accent-[#1B2E7A]"
                    data-testid="editor-node-ending"
                  />
                  Ending node
                </label>
                {focusedNode.id !== scenario.start_node_id && (
                  <button
                    type="button"
                    onClick={() => removeNode(focusedNode.id)}
                    className="text-xs text-[#DC2626] hover:text-[#991B1B] inline-flex items-center gap-1"
                    data-testid="editor-node-delete"
                  >
                    <Trash2 className="h-3 w-3" /> Delete node
                  </button>
                )}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs text-[#374151] mb-1">Prompt (situation shown to the user)</label>
              <Textarea
                value={focusedNode.prompt}
                onChange={(e) => patchNode(focusedNode.id, { prompt: e.target.value })}
                rows={3}
                placeholder='e.g. "Customer is looking at the Wrangler boots and ignores your greeting..."'
                data-testid="editor-node-prompt"
              />
            </div>

            {focusedNode.ending && (
              <div className="mb-4">
                <label className="block text-xs text-[#374151] mb-1">Ending label (outcome shown in result)</label>
                <Input
                  value={focusedNode.ending_label}
                  onChange={(e) => patchNode(focusedNode.id, { ending_label: e.target.value })}
                  placeholder="e.g. Sale closed · $4,500"
                  data-testid="editor-node-ending-label"
                />
              </div>
            )}

            {!focusedNode.ending && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-medium">Options</p>
                  <button
                    type="button"
                    onClick={() => addOption(focusedNode.id)}
                    className="text-xs text-[#1B2E7A] hover:underline inline-flex items-center gap-1"
                    data-testid="editor-add-option"
                  >
                    <Plus className="h-3 w-3" /> Add option
                  </button>
                </div>

                <div className="space-y-3">
                  {focusedNode.options.length === 0 && (
                    <p className="text-xs text-[#9CA3AF] italic">No options yet. Add at least one (or mark as ending).</p>
                  )}
                  {focusedNode.options.map((opt, idx) => (
                    <div
                      key={opt.id}
                      className={`p-3 rounded-lg border ${opt.is_correct ? "border-[#1B2E7A] bg-[#EFF6FF]" : "border-[#D1D5DB] bg-white"}`}
                      data-testid={`editor-option-${idx}`}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <div className="h-6 w-6 rounded-full bg-[#F3F4F6] text-[#374151] text-xs flex items-center justify-center font-bold shrink-0 mt-1">
                          {String.fromCharCode(65 + idx)}
                        </div>
                        <Input
                          value={opt.text}
                          onChange={(e) => patchOption(focusedNode.id, opt.id, { text: e.target.value })}
                          placeholder="What the user clicks"
                          className="flex-1"
                          data-testid={`editor-option-text-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeOption(focusedNode.id, opt.id)}
                          className="text-[#DC2626] hover:text-[#991B1B] p-1.5"
                          data-testid={`editor-option-delete-${idx}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mb-2 ml-8">
                        <label className="text-xs inline-flex items-center gap-1.5 text-[#374151] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={opt.is_correct}
                            onChange={(e) => patchOption(focusedNode.id, opt.id, { is_correct: e.target.checked })}
                            className="h-3.5 w-3.5 accent-[#1B2E7A]"
                            data-testid={`editor-option-correct-${idx}`}
                          />
                          <CheckCircle2 className="h-3 w-3 text-[#1B2E7A]" /> Best-practice answer
                        </label>
                        <div className="inline-flex items-center gap-2">
                          <label className="text-xs text-[#374151]">Points:</label>
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            value={opt.points}
                            onChange={(e) => patchOption(focusedNode.id, opt.id, { points: Number(e.target.value) })}
                            className="h-8 w-20 text-xs"
                            data-testid={`editor-option-points-${idx}`}
                          />
                        </div>
                      </div>

                      <div className="ml-8 mb-2">
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-[#6B7280] mb-1">Feedback shown after pick</label>
                        <Input
                          value={opt.feedback}
                          onChange={(e) => patchOption(focusedNode.id, opt.id, { feedback: e.target.value })}
                          placeholder="Why this answer is good / bad"
                          data-testid={`editor-option-feedback-${idx}`}
                        />
                      </div>

                      <div className="ml-8">
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-[#6B7280] mb-1">Next node</label>
                        <select
                          value={opt.next_node_id || ""}
                          onChange={(e) => patchOption(focusedNode.id, opt.id, { next_node_id: e.target.value })}
                          className="w-full border border-[#D1D5DB] rounded-md px-3 py-2 text-sm bg-white"
                          data-testid={`editor-option-next-${idx}`}
                        >
                          <option value="">— End scenario here —</option>
                          {scenario.nodes
                            .filter((n) => n.id !== focusedNode.id)
                            .map((n) => (
                              <option key={n.id} value={n.id}>
                                {n.id}{n.ending ? " · ending" : ""}{n.prompt ? ` — ${n.prompt.slice(0, 40)}...` : ""}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
